import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Breadcrumb } from '../../components/Breadcrumb';
import { Loader } from '../../components/Loader';
import { API } from '../../axios/instance';
import { fetchContacts } from '../../redux/contactStore/action';
import { getUserContext } from '../../utils/auth';

const FALLBACK_USER_ID = '6897acb7f2bc1479c645102d';
const DEFAULT_BUSINESS_ID = '6897b362dce30c2c9892b759';

export function LedgerMain() {
  const dispatch = useDispatch();
  const rawContacts = useSelector((s) => s.contact?.data);
  const contacts = useMemo(() => rawContacts || [], [rawContacts]);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [balance, setBalance] = useState(0);

  const [type, setType] = useState('all');
  const [contactId, setContactId] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const userContext = getUserContext();
  const effectiveUserId = userContext.userId || FALLBACK_USER_ID;
  const effectiveBusinessId = userContext.businessId || DEFAULT_BUSINESS_ID;

  useEffect(() => {
    if (!effectiveUserId) return;
    if (!contacts || contacts.length === 0) {
      dispatch(fetchContacts({
        userId: effectiveUserId,
        businessId: effectiveBusinessId,
      }));
    }
  }, [dispatch, contacts, effectiveUserId, effectiveBusinessId]);

  const load = useCallback(async () => {
    if (!effectiveUserId) {
      setItems([]); setTotal(0); setBalance(0);
      return;
    }
    setLoading(true); setError('');
    try {
      const params = { page, limit };
      if (type !== 'all') params.type = type;
      if (contactId !== 'all') params.contact = contactId;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      params.userId = effectiveUserId;
      params.businessId = effectiveBusinessId;
      const res = await API.get('invoice/invoice', { params });
      const payload = res.data;
      if (payload && Array.isArray(payload.items)) {
        setItems(payload.items);
        setTotal(Number(payload.total || 0));
        setBalance(Number(payload.balance || 0));
      } else if (Array.isArray(payload)) {
        setItems(payload);
        setTotal(payload.length);
        // Fallback balance calculation when server didn't paginate
        const bal = payload.reduce((sum, inv) => sum + (inv.type === 'expense' ? -Number(inv.amount || 0) : Number(inv.amount || 0)), 0);
        setBalance(bal);
      } else {
        setItems([]); setTotal(0);
        setBalance(0);
      }
    } catch (e) {
      setError('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [page, limit, type, contactId, dateFrom, dateTo, effectiveUserId, effectiveBusinessId]);

  useEffect(() => { load(); }, [load]);

  const resetAndLoad = () => {
    if (page !== 1) {
      setPage(1);
    } else {
      load();
    }
  };

  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState('');
  const [form, setForm] = useState({ type: 'income', amount: '', date: '', description: '', contactId: 'none' });

  const save = async () => {
    if (!effectiveUserId || !effectiveBusinessId) {
      setSaveErr('Missing user or business context.');
      return;
    }
    setSaving(true); setSaveErr('');
    try {
      const payload = {
        userId: effectiveUserId,
        businessId: effectiveBusinessId,
        contactId: form.contactId === 'none' ? null : form.contactId,
        type: form.type,
        amount: parseFloat(form.amount),
        currency: 'BDT',
        date: form.date || undefined,
        description: form.description || ''
      };
      await API.post('invoice/invoice', payload);
      setShowNew(false);
      setForm({ type: 'income', amount: '', date: '', description: '', contactId: 'none' });
      resetAndLoad();
    } catch (e) {
      setSaveErr('Failed to save');
    } finally { setSaving(false); }
  };

  const totalPages = Math.max(1, Math.ceil((total || 0) / limit));

  return (
    <div className="container">
      <div className="d-flex align-items-center justify-content-between">
        <Breadcrumb items={[{ label: 'Home', path: '/dashboard' }, { label: 'Ledger' }]} />
        {loading ? <Loader size="sm" /> : null}
      </div>

      <div className="row g-2 align-items-end mb-3">
        <div className="col-sm-3">
          <label className="form-label mb-1">Type</label>
          <select className="form-select" value={type} onChange={(e)=>setType(e.target.value)}>
            <option value="all">All</option>
            <option value="income">Income</option>
            <option value="expense">Expense</option>
          </select>
        </div>
        <div className="col-sm-4">
          <label className="form-label mb-1">Contact</label>
          <select className="form-select" value={contactId} onChange={(e)=>setContactId(e.target.value)}>
            <option value="all">All</option>
            {contacts.map(c => (
              <option key={c._id} value={c._id}>{c.contactName || c.contactPhone || c.contactEmail || String(c._id).slice(0,6)}</option>
            ))}
          </select>
        </div>
        <div className="col-sm-2">
          <label className="form-label mb-1">From</label>
          <input type="date" className="form-control" value={dateFrom} onChange={(e)=>setDateFrom(e.target.value)} />
        </div>
        <div className="col-sm-2">
          <label className="form-label mb-1">To</label>
          <input type="date" className="form-control" value={dateTo} onChange={(e)=>setDateTo(e.target.value)} />
        </div>
        <div className="col-sm-1 d-grid">
          <button className="btn btn-primary" onClick={resetAndLoad}>Filter</button>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-header d-flex align-items-center justify-content-between">
          <h5 className="mb-0">Invoices</h5>
          <div className="d-flex align-items-center gap-2">
            <span className="badge bg-light text-dark">Balance: BDT {Number(balance || 0).toLocaleString()}</span>
            <button className="btn btn-sm btn-success" onClick={()=> setShowNew(v=>!v)}>{showNew ? 'Close' : 'New Transaction'}</button>
          </div>
        </div>
        <div className="card-body">
          {showNew && (
            <div className="border rounded p-2 mb-3">
              <div className="row g-2 align-items-end">
                <div className="col-sm-2">
                  <label className="form-label mb-1">Type</label>
                  <select className="form-select" value={form.type} onChange={(e)=>setForm(f=>({...f, type: e.target.value}))}>
                    <option value="income">Income</option>
                    <option value="expense">Expense</option>
                  </select>
                </div>
                <div className="col-sm-2">
                  <label className="form-label mb-1">Amount</label>
                  <input className="form-control" type="number" min="0" step="0.01" value={form.amount} onChange={(e)=>setForm(f=>({...f, amount: e.target.value}))} />
                </div>
                <div className="col-sm-3">
                  <label className="form-label mb-1">Date</label>
                  <input className="form-control" type="date" value={form.date} onChange={(e)=>setForm(f=>({...f, date: e.target.value}))} />
                </div>
                <div className="col-sm-5">
                  <label className="form-label mb-1">Contact</label>
                  <select className="form-select" value={form.contactId} onChange={(e)=>setForm(f=>({...f, contactId: e.target.value}))}>
                    <option value="none">None</option>
                    {contacts.map(c => (
                      <option key={c._id} value={c._id}>{c.contactName || c.contactPhone || c.contactEmail || String(c._id).slice(0,6)}</option>
                    ))}
                  </select>
                </div>
                <div className="col-sm-12">
                  <label className="form-label mb-1">Description</label>
                  <textarea className="form-control" rows={2} value={form.description} onChange={(e)=>setForm(f=>({...f, description: e.target.value}))} placeholder="Optional details" />
                </div>
                {saveErr && (<div className="col-sm-12 text-danger small">{saveErr}</div>)}
                <div className="col-sm-12 d-flex justify-content-end gap-2">
                  <button className="btn btn-outline-secondary" onClick={()=>{ setShowNew(false); setSaveErr(''); }}>Cancel</button>
                  <button className="btn btn-success" disabled={saving || !(parseFloat(form.amount) > 0)} onClick={save}>
                    {saving ? 'Saving...' : 'Save Transaction'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {error && <div className="text-danger mb-2">{error}</div>}
          <div className="table-responsive">
            <table className="table table-sm align-middle">
              <thead>
                <tr>
                  <th style={{width:'18%'}}>Date</th>
                  <th style={{width:'12%'}}>Type</th>
                  <th style={{width:'15%'}} className="text-end">Amount</th>
                  <th style={{width:'25%'}}>Contact</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {items.map(inv => {
                  const contact = contacts.find(c => c._id === (inv.contactId && (inv.contactId._id || inv.contactId)));
                  return (
                    <tr key={inv._id}>
                      <td>{new Date(inv.date || inv.createdAt).toLocaleString()}</td>
                      <td><span className={inv.type==='income' ? 'badge bg-success' : 'badge bg-danger'}>{inv.type}</span></td>
                      <td className="text-end">{inv.type==='expense' ? '-' : ''}{Number(inv.amount || 0).toLocaleString()}</td>
                      <td>{contact ? (contact.contactName || contact.contactPhone || contact.contactEmail) : '-'}</td>
                      <td style={{whiteSpace:'pre-wrap'}}>{inv.description || '-'}</td>
                    </tr>
                  );
                })}
                {items.length === 0 && (
                  <tr><td colSpan={5} className="text-muted">No invoices found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="d-flex justify-content-between align-items-center">
            <div className="text-muted small">Page {page} of {totalPages} • Total {total}</div>
            <div className="btn-group">
              <button className="btn btn-outline-secondary btn-sm" disabled={page<=1} onClick={()=> setPage(p=>Math.max(1,p-1))}>Prev</button>
              <button className="btn btn-outline-secondary btn-sm" disabled={page>=totalPages} onClick={()=> setPage(p=>Math.min(totalPages,p+1))}>Next</button>
            </div>
            <div>
              <select className="form-select form-select-sm" style={{width: 'auto'}} value={limit} onChange={(e)=>{ setLimit(parseInt(e.target.value,10)||10); setPage(1); }}>
                {[10,20,50,100].map(n=> <option key={n} value={n}>{n}/page</option>)}
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
